import { http } from '@app/lib/api'
import { useAuth } from '@app/lib/auth'
import { MARKETING_OPT_IN_LABEL, type MarketingPreferences } from '@shared/contracts/marketing'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'

/** The signed-in member's email preferences (the portal profile and the opt-in share it). */
export function useMyEmailPreferences(enabled = true) {
  const { user } = useAuth()
  return useQuery({
    queryKey: ['portal', 'marketing'],
    queryFn: () => http.get<MarketingPreferences>('/api/v1/portal/marketing/preferences'),
    enabled: enabled && !!user,
    retry: false,
    staleTime: 60_000,
  })
}

/**
 * "Send me BTown Pride news" — the one opt-in every form uses. Unchecked by default; hidden for a
 * signed-in member who already gets the newsletter. Controlled (`checked` + `onChange`) for
 * forms kept in state, or uncontrolled for forms read with FormData (it then submits
 * `marketingOptIn=true` when ticked).
 */
export function MarketingOptIn({
  checked,
  onChange,
}: {
  checked?: boolean
  onChange?: (checked: boolean) => void
}) {
  const { user } = useAuth()
  const id = useId()
  const prefs = useMyEmailPreferences()
  const subscribed =
    !!user &&
    prefs.data?.status === 'subscribed' &&
    prefs.data.lists.some((l) => l.key === 'newsletter' && l.subscribed)
  if (subscribed) return null
  return (
    <div className="checkbox marketing-opt-in">
      {onChange ? (
        <input
          id={id}
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      ) : (
        <input id={id} type="checkbox" name="marketingOptIn" value="true" />
      )}
      <label htmlFor={id}>{MARKETING_OPT_IN_LABEL}</label>
    </div>
  )
}

/** Off-screen field bots fill in and people never see (the server ignores such submissions). */
export function Honeypot({ name = 'homepage' }: { name?: string }) {
  return (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
    >
      <label>
        Homepage
        <input name={name} tabIndex={-1} autoComplete="off" />
      </label>
    </div>
  )
}

/** Footer newsletter signup: email and optional first name, confirmed inline. */
export function NewsletterSignup() {
  const id = useId()
  const status = useRef<HTMLParagraphElement>(null)
  const [error, setError] = useState<string | null>(null)
  const signup = useMutation({
    mutationFn: (v: { email: string; firstName: string | null; homepage?: string }) =>
      http.post<{ ok: true }>('/api/v1/public/marketing/subscribe', v),
    onSuccess: () => {
      setError(null)
      window.setTimeout(() => status.current?.focus())
    },
    onError: (e) => setError((e as Error).message),
  })
  if (signup.isSuccess)
    return (
      <div className="newsletter-signup">
        <p className="newsletter-signup__done mb-0" role="status" tabIndex={-1} ref={status}>
          Thanks! You’re on the list. Check your inbox for a welcome email with a link to choose
          what you get.
        </p>
      </div>
    )
  return (
    <form
      className="newsletter-signup stack--sm"
      aria-labelledby={`${id}-h`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        const data = new FormData(e.currentTarget)
        const email = String(data.get('email') ?? '').trim()
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setError('Enter an email address like name@example.com')
          document.getElementById(`${id}-email`)?.focus()
          return
        }
        signup.mutate({
          email,
          firstName: String(data.get('firstName') ?? '').trim() || null,
          homepage: String(data.get('homepage') ?? '') || undefined,
        })
      }}
    >
      <strong id={`${id}-h`}>Queer news sent not straight to your inbox</strong>
      <p className="small mb-0">(See what we did there?)</p>
      <div className="newsletter-signup__fields">
        <div className="field mb-0">
          <label className="field__label" htmlFor={`${id}-email`}>
            Email
          </label>
          <input
            id={`${id}-email`}
            className="input"
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-err` : undefined}
          />
        </div>
        <div className="field mb-0">
          <label className="field__label" htmlFor={`${id}-name`}>
            First name <span className="faint">(optional)</span>
          </label>
          <input
            id={`${id}-name`}
            className="input"
            name="firstName"
            autoComplete="given-name"
            maxLength={100}
          />
        </div>
      </div>
      <Honeypot />
      {error ? (
        <div className="field__error" id={`${id}-err`} role="alert">
          {error}
        </div>
      ) : null}
      <button type="submit" className="btn btn--sm" disabled={signup.isPending}>
        {signup.isPending ? 'Signing up…' : 'Sign up'}
      </button>
    </form>
  )
}

/**
 * List on/off checkboxes with Save and "Unsubscribe from everything". Used by the emailed
 * preferences link and the portal profile.
 */
export function PreferencesForm({
  prefs,
  saving,
  onSave,
  heading = 'Email lists',
}: {
  prefs: MarketingPreferences
  saving: boolean
  onSave: (input: { lists?: Record<string, boolean>; unsubscribeAll?: boolean }) => void
  heading?: string
}) {
  const id = useId()
  const [choice, setChoice] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(prefs.lists.map((l) => [l.key, l.subscribed])),
  )
  const anyOn = prefs.lists.some((l) => l.subscribed)
  return (
    <form
      className="stack--sm"
      onSubmit={(e) => {
        e.preventDefault()
        onSave({ lists: choice })
      }}
    >
      <fieldset className="stack--sm" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="field__label">{heading}</legend>
        {prefs.lists.map((l) => (
          <label key={l.key} className="checkbox" htmlFor={`${id}-${l.key}`}>
            <input
              id={`${id}-${l.key}`}
              type="checkbox"
              checked={!!choice[l.key]}
              onChange={(e) => setChoice((c) => ({ ...c, [l.key]: e.target.checked }))}
              aria-describedby={l.description ? `${id}-${l.key}-d` : undefined}
            />
            <span>
              <strong>{l.name}</strong>
              {l.description ? (
                <span id={`${id}-${l.key}-d`} className="small muted" style={{ display: 'block' }}>
                  {l.description}
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>
      <div className="cluster">
        <button type="submit" className="btn" disabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
        {anyOn ? (
          <button
            type="button"
            className="btn btn--secondary"
            disabled={saving}
            onClick={() => onSave({ unsubscribeAll: true })}
          >
            Unsubscribe from everything
          </button>
        ) : null}
      </div>
      <p className="tiny muted mb-0">
        Receipts, tax statements and messages about your applications, orders and shifts are
        separate and keep coming.
      </p>
    </form>
  )
}

/** Portal profile: the signed-in member's email lists, keyed by their sign-in email. */
export function MyEmailPreferencesCard() {
  const id = useId()
  const qc = useQueryClient()
  const q = useMyEmailPreferences()
  const [saved, setSaved] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (input: { lists?: Record<string, boolean>; unsubscribeAll?: boolean }) =>
      http.put<MarketingPreferences>('/api/v1/portal/marketing/preferences', input),
    onSuccess: (p) => {
      qc.setQueryData(['portal', 'marketing'], p)
      setSaved(p.status === 'subscribed' ? 'Email preferences saved.' : 'You’re unsubscribed.')
    },
    onError: (e) => setSaved((e as Error).message),
  })
  return (
    <section className="card stack--sm" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="card__title mb-0">
        Email preferences
      </h2>
      <p className="small muted mb-0">
        News and updates sent to {q.data?.email ?? 'your sign-in email'}.
      </p>
      {q.data ? (
        <PreferencesForm
          key={JSON.stringify(q.data.lists)}
          prefs={q.data}
          saving={save.isPending}
          heading="Send me"
          onSave={(input) => {
            setSaved(null)
            save.mutate(input)
          }}
        />
      ) : q.isLoading ? (
        <p className="small muted mb-0">Loading…</p>
      ) : (
        <p className="small muted mb-0">Your email preferences couldn’t be loaded.</p>
      )}
      <p role="status" className="small bold mb-0">
        {saved}
      </p>
    </section>
  )
}
